import {
  DashboardChecksTypeEnum,
  FileTypeEnum,
  UserDocumentModel,
  accountApis,
  apiConfigs,
  fileApis,
  uploadApis,
} from "@payping/api";
import { Add, Close, RedoOutlined, Upload as UploadIcon } from "@payping/icons";
import { getPersonalInfo, getUserChecks, getUserPhoneNumber, updatePersonalInfo } from "@payping/redux/actions";
import { SDKDateObject, dateFormats, isBeforeToday, toJalali, toUtc, utcNow } from "@payping/utils/dateUtils";
import { getFileName, getFileType } from "@payping/utils/string";
import { ValidEmail, ValidMobileNumber, ValidNationalId, ValidPersianName } from "@payping/utils/validation";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Page,
  Row,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsyncRetry, useDeepCompareEffect } from "react-use";

import { FormLayout } from "@/components/DashUser/FormLayout";
import { SubLayout } from "@/components/SubLayout";
import { useAppDispatch, useAppSelector, useLastUrlWasPage } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DashboardRoutes } from "@/router/constant";

const PersonalEdit: React.FC = () => {
  const currentURL = useLocation().pathname;
  const isRegisterURL = currentURL === DashboardRoutes.register.personal.url;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showCodeForm, setShowCodeForm] = useState<boolean>(false);
  const [removeProfileCalled, setRemoveProfileCalled] = useState<boolean>(false);
  const [removeDocCalled, setRemoveDocCalled] = useState<boolean>(false);
  const [nationalFilesList, setNationalFilesList] = useState<UploadFile[]>([]);
  const [defaultBirthPickerValue, setDefaultBirthPickerValue] = useState<SDKDateObject>();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { t } = useAppTranslation();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const profilePicture = useAppSelector((state) => state.user.personalInfo?.profilePicture);
  const personalDocuments = useAppSelector((state) => state.user.personalInfo?.documents);
  const isOfficialSubmitted = useAppSelector((state) => state.user.businessInfo?.isOfficialSubmited);
  const checks = useAppSelector((state) => state.user.checks);
  // const personalSectionStatus = Math.min(Number(checks?.personalStatus), Number(checks?.birthCertificateStatus))df;
  const isPersonalDisabled = checks?.personalStatus === DashboardChecksTypeEnum.Confirmed;
  // TODO: this should merge with isPersonalDisabled (back-end should do it)
  const isBirthCertDisabled = checks?.birthCertificateStatus === DashboardChecksTypeEnum.Confirmed;
  const navigate = useNavigate();
  const { navigateToLast } = useLastUrlWasPage();

  useMessagedAsync(() => dispatch(getUserChecks()), [isPersonalDisabled], { useProgress: true });

  const [, removeDocument] = useMessagedAsyncFn(
    (id) => fileApis.removeDocumentWithIdWithCancellationTokenDelete(id),
    [],
    undefined,
    {
      useProgress: true,
    },
  );

  const { loading } = useAsyncRetry(async () => {
    await dispatch(getPersonalInfo());
    await dispatch(getUserPhoneNumber());
  }, [dispatch]);

  const utcDate =
    personalInfo?.birthDate && personalInfo?.birthDate !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: personalInfo?.birthDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;

  useDeepCompareEffect(() => {
    const ProfileInitialValues = {
      avatar: profilePicture,
      birthCertificateNumber: personalInfo?.birthCertificateNumber,
      birthDate: utcDate,
      email: personalInfo?.email,
      fatherName: personalInfo?.fatherName,
      firstName: personalInfo?.firstName,
      lastName: personalInfo?.lastName,
      nationalCode: personalInfo?.nationalCode,
      phoneNumber: personalInfo?.phoneNumber,
    };
    setDefaultBirthPickerValue(utcDate || utcNow({ toFormat: "YYYY" }).object.subtract(18, "years"));
    form.setFieldsValue(ProfileInitialValues);
  }, [profilePicture, personalInfo, utcDate]);

  useEffect(() => {
    if (profilePicture) {
      setFileList([
        {
          name: "image",
          size: 0,
          type: "image/*",
          uid: "0",
          url: profilePicture.replace("payping.ir", apiConfigs.domain),
        },
      ]);
    }
    if (personalDocuments) {
      setNationalFilesList(
        personalDocuments.map(({ serverName }: UserDocumentModel, index: number) => {
          const name = serverName || "file";
          return {
            name: `${getFileName(name)} ${index + 1}`,
            size: 0,
            type: getFileType(name),
            uid: String(index),
            url: name.replace("payping.ir", apiConfigs.domain),
          };
        }),
      );
    }
    return () => {
      setFileList([]);
      setNationalFilesList([]);
    };
  }, [profilePicture, personalDocuments]);

  const [{ loading: verifyPhoneNumberLoading, error: errorVerifyPhone, value: successVerifyPhone }, verifyPhoneNumber] =
    useMessagedAsyncFn(
      async () => {
        await form.validateFields();
        const response = await accountApis.verifyPhoneNumberWithRequestModelPost({
          code: form.getFieldValue("code"),
          phoneNumber: form.getFieldValue("phoneNumber"),
        });
        setShowCodeForm(false);
        return response;
      },
      [],
      undefined,
      { loading: false, success: t("profile.personal.successVerifyPhone") },
    );

  const setTimer = () => {
    let oneMin = 59;
    form.setFieldsValue({ timer: oneMin });
    const interval = setInterval(() => {
      oneMin -= 1;
      if (oneMin < 1) clearInterval(interval);
      form.setFieldsValue({ timer: oneMin.toString() });
    }, 1000);
  };

  const [{ loading: getCodeLoading }, onCLickGetCode] = useMessagedAsyncFn(
    async () => {
      await form.validateFields(["phoneNumber"]);
      await accountApis.phoneNumberWithRequestModelPost({
        phoneNumber: form.getFieldValue("phoneNumber"),
      });
      setShowCodeForm(true);
      setTimer();
    },
    [],
    undefined,
    { success: t("profile.personal.successSendCode") },
  );

  const [{ loading: sendingEmailVerify }, sendEmailVerify] = useMessagedAsyncFn(
    async () => {
      await form.validateFields(["email"]);
      await accountApis.sendVerificationEmailGet(/* form.getFieldValue("email") */);
    },
    [],
    undefined,
    { success: t("profile.personal.successSendEmail") },
  );

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: any) => {
      const { avatar, documents, ...data } = values;
      const profilePictureFileName =
        typeof avatar === "string" ? avatar.split("/")[avatar.split("/").length - 1] : avatar?.fileList[0]?.response;
      const birthDate = toUtc({
        convertTime: false,
        date: data?.birthDate,
        toFormat: dateFormats.standard.utcDate,
      }).string;
      const finalPersonalInfo = {
        ...data,
        avatar: profilePictureFileName,
        birthDate,
      };
      await dispatch(updatePersonalInfo(finalPersonalInfo));
      // TODO: the method for uploading user profile picture SHOULD change in backend (upload project)
      if (removeProfileCalled) {
        await fileApis.removeProfilePictureWithCancellationTokenDelete();
      }
      if (profilePictureFileName) {
        await fileApis.addFileWithRequestModelWithCancellationTokenPost({
          fileName: profilePictureFileName,
          type: FileTypeEnum.ProfilePicture,
        });
      }
      if (removeDocCalled && personalDocuments) {
        await Promise.all(
          personalDocuments.map(async (doc: UserDocumentModel) => {
            await removeDocument(doc.id);
          }),
        );
      }
      if (documents) {
        await Promise.all(
          documents.fileList.map(async (doc: UploadFile) => {
            const fileName = doc.url ? doc.url.split("/").pop() : doc.response;
            if (fileName)
              await fileApis.addFileWithRequestModelWithCancellationTokenPost({
                fileName,
                type: FileTypeEnum.ShensanameKartMelli,
              });
          }),
        );
      }
      if (isRegisterURL) navigate(DashboardRoutes.register.business.url);
      if (!isRegisterURL) navigate(DashboardRoutes.settings.personal.root.url);
    },
    [removeProfileCalled, removeDocCalled, personalDocuments],
    undefined,
    { useProgress: true },
  );

  return (
    <Drawer
      title={isRegisterURL ? t("settings.business.paymentGatewayRequestForm") : t("settings.business.editForm")}
      open={true}
      height={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={() => navigateToLast()} />}
    >
      <Page>
        <SubLayout>
          <FormLayout
            title={
              <Typography.Title className={"!tw-m-0 tw-mb-4"} level={4}>
                {t("profile.main.personal")}
              </Typography.Title>
            }
            instructions={
              <>
                <p>{t("settings.personal.instructions.firstNameAndLastName")}</p>
                <p>{t("settings.personal.instructions.nationalIdAndBirthdate")}</p>
                <p>{t("settings.personal.instructions.nationalIdAndBirthdatePhoneNumber")}</p>
                {isOfficialSubmitted ? <p>{t("settings.personal.instructions.legalAttention")}</p> : null}
              </>
            }
          >
            <Form form={form} onFinish={onSubmit} scrollToFirstError={{ behavior: "smooth" }} layout={"vertical"}>
              <Form.Item name="avatar">
                <Upload
                  disabled={loading}
                  listType={"picture-circle"}
                  requestFunction={uploadApis.profilePicWithFilePost}
                  onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
                  fileList={fileList}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                  onRemove={() => setRemoveProfileCalled(true)}
                  allowedExtensions={["jpg", "jpeg", "png"]}
                  showTooltip
                >
                  {!fileList.length && (
                    <div>
                      <Add width={"20px"} />
                      <div className={"ant-upload-text"}>{t("profile.avatar")}</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              <Form.Item
                label={t("firstName")}
                name="firstName"
                rules={[
                  () => ({
                    validator(_, value): Promise<void> {
                      if (ValidPersianName(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("profile.validFirstName")));
                    },
                  }),
                  { required: true },
                ]}
              >
                <Input skeleton={loading} disabled={!form.getFieldValue("firstName") ? false : isPersonalDisabled} />
              </Form.Item>
              <Form.Item
                label={t("lastName")}
                name="lastName"
                rules={[
                  {
                    validator(_, value): Promise<void> {
                      if (ValidPersianName(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("profile.validLastName")));
                    },
                  },
                  { required: true },
                ]}
              >
                <Input skeleton={loading} disabled={!form.getFieldValue("lastName") ? false : isPersonalDisabled} />
              </Form.Item>

              <Row gutter={[8, 0]}>
                <Col span={12}>
                  <Form.Item
                    label={t("nationalCode")}
                    name="nationalCode"
                    rules={[
                      () => ({
                        validator(_, value): Promise<void> {
                          if (ValidNationalId(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("profile.validBNationalCode")));
                        },
                      }),
                      { message: t("error.requiredForTaxCode", { name: t("nationalCode") }), required: true },
                    ]}
                  >
                    <Input
                      skeleton={loading}
                      disabled={!form.getFieldValue("nationalCode") ? false : isPersonalDisabled}
                      onlyNumbers
                      canStartWithZero
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t("birthDate")}
                    name="birthDate"
                    rules={[
                      { required: true },
                      {
                        validator(_, value) {
                          if (value && !isBeforeToday(value)) {
                            return Promise.reject(new Error(t("profile.birthDateValidation")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      className={"tw-w-full"}
                      skeleton={loading}
                      disabled={!form.getFieldValue("birthDate") ? false : isPersonalDisabled}
                      disabledDate={(date) => !isBeforeToday(date)}
                      defaultPickerValue={defaultBirthPickerValue}
                      dateOrder={["YearPicker", "MonthPicker", "DatePicker"]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t("mobile")}
                    shouldUpdate={(prev, next) => prev.phoneNumber !== next.phoneNumber || prev.timer !== next.timer}
                    required
                  >
                    {(phoneNumberInputField) => (
                      <Row gutter={[8, 8]}>
                        <Col xs={24} lg={24}>
                          <Form.Item
                            noStyle
                            rules={[
                              () => ({
                                validator(_, value) {
                                  if (ValidMobileNumber(value)) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error(t("profile.validPhoneNumber")));
                                },
                              }),
                              { required: true },
                            ]}
                            name={"phoneNumber"}
                          >
                            <Input
                              skeleton={loading}
                              disabled={
                                isPersonalDisabled || phoneNumberInputField.getFieldValue("timer") > 0 || showCodeForm
                              }
                              maxLength={11}
                              onlyNumbers
                              canStartWithZero
                            />
                          </Form.Item>
                        </Col>
                        {successVerifyPhone?.verificationSucceeded && (
                          <Col xs={24} lg={24}>
                            <Typography.Text type={"success"}>{successVerifyPhone.message}</Typography.Text>
                          </Col>
                        )}
                        {(!personalInfo?.phoneNumberConfirmed ||
                          phoneNumberInputField.getFieldValue("phoneNumber") !== personalInfo?.phoneNumber) &&
                          !successVerifyPhone?.verificationSucceeded && (
                            <Col xs={24} lg={10}>
                              <Form.Item noStyle>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  loading={loading || getCodeLoading}
                                  disabled={
                                    form.getFieldValue("timer") > 0 ||
                                    showCodeForm ||
                                    form.isFieldsTouched(true) ||
                                    phoneNumberInputField.getFieldValue("phoneNumber")?.length < 11 ||
                                    isPersonalDisabled
                                  }
                                  onClick={onCLickGetCode}
                                >
                                  {phoneNumberInputField.getFieldValue("timer") > 0 ? (
                                    <Flex gap={"small"}>
                                      <Typography.Text>{t("nextRequestUntil")}</Typography.Text>
                                      <Typography.Text>{phoneNumberInputField.getFieldValue("timer")}</Typography.Text>
                                      <Typography.Text>{t("anotherSec")}</Typography.Text>
                                    </Flex>
                                  ) : (
                                    t("profile.verifyPhoneNumber")
                                  )}
                                </Button>
                              </Form.Item>
                            </Col>
                          )}
                      </Row>
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={t("profile.birthCertificateNumber")}
                    name="birthCertificateNumber"
                    rules={[{ required: true }]}
                  >
                    <Input
                      skeleton={loading}
                      disabled={!form.getFieldValue("birthCertificateNumber") ? false : isBirthCertDisabled}
                      onlyNumbers
                      canStartWithZero
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={t("profile.fatherName")} name="fatherName" rules={[{ required: true }]}>
                    <Input
                      skeleton={loading}
                      disabled={!form.getFieldValue("fatherName") ? false : isBirthCertDisabled}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label={t("email")} shouldUpdate={(prev, next) => prev.email !== next.email} required>
                {(tmpForm) => (
                  <Row gutter={[8, 8]}>
                    <Col xs={24} lg={14}>
                      <Form.Item
                        name="email"
                        rules={[
                          () => ({
                            validator(_, value) {
                              if (ValidEmail(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(t("profile.validEmail")));
                            },
                          }),
                          { required: true },
                        ]}
                        noStyle
                        normalize={(value) => value.trim()}
                      >
                        <Input
                          skeleton={loading}
                          disabled={!form.getFieldValue("email") ? false : isPersonalDisabled}
                        />
                      </Form.Item>
                    </Col>
                    {(!personalInfo?.emailVerified || tmpForm.getFieldValue("email") !== personalInfo?.email) && (
                      <Col xs={24} lg={10}>
                        <Form.Item noStyle>
                          <Button
                            variant="outlined"
                            color="primary"
                            loading={loading || sendingEmailVerify}
                            disabled={form.isFieldsTouched(true) || isPersonalDisabled}
                            onClick={sendEmailVerify}
                          >
                            {t("profile.verifyEmail")}
                          </Button>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues): boolean => prevValues.code !== currentValues.code}
              >
                {(modalForm) => (
                  <Modal
                    title={t("profile.verifyPhoneNumber")}
                    open={showCodeForm}
                    okButtonProps={{
                      disabled: !modalForm.getFieldValue("code") || modalForm.getFieldValue("code").length < 6,
                      loading: loading || verifyPhoneNumberLoading,
                    }}
                    onOk={async () => {
                      await verifyPhoneNumber();
                      form.resetFields(["code"]);
                    }}
                    onCancel={() => {
                      form.resetFields(["code"]);
                      setShowCodeForm(false);
                    }}
                    destroyOnClose
                    maskClosable={false}
                  >
                    <Divider className={"color--gray300 !tw-mt-0"} />
                    <Row gutter={[8, 16]} className={"tw-mt-4"}>
                      <Col span={24}>
                        <Typography.Text className="tw-font-medium">{t("profile.enterCode")}</Typography.Text>
                      </Col>
                      <Col xs={24} lg={16}>
                        <Form.Item name="code" label={t("profile.sixDigitCode")} required>
                          <Input
                            maxLength={6}
                            placeholder={t("enterCode")}
                            skeleton={loading || verifyPhoneNumberLoading}
                            onlyNumbers
                            canStartWithZero
                          />
                        </Form.Item>

                        {errorVerifyPhone?.name === "BadRequestError" && (
                          <Typography.Text type={"danger"} className={"tw-text-center"}>
                            {t("error.wrongCode")}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} lg={8}>
                        <Form.Item
                          shouldUpdate={(prevValues, currentValues): boolean =>
                            prevValues.timer !== currentValues.timer
                          }
                        >
                          {({ getFieldValue }) => {
                            const remaining = getFieldValue("timer");
                            if (parseInt(remaining, 10) > 0) {
                              return (
                                <Form.Item name={"timer"}>
                                  <Typography.Text>
                                    {remaining} {t("second")}{" "}
                                  </Typography.Text>
                                </Form.Item>
                              );
                            }
                            return (
                              <Button
                                size="small"
                                icon={<RedoOutlined width={14} height={14} />}
                                loading={getCodeLoading}
                                onClick={onCLickGetCode}
                                type="link"
                              >
                                {t("sendAgain")}
                              </Button>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Modal>
                )}
              </Form.Item>
              <Form.Item name={"documents"}>
                <Upload
                  disabled={loading}
                  crop={false}
                  listType={"picture-card"}
                  requestFunction={uploadApis.miscWithFilePost}
                  onChange={(info: UploadChangeParam): void => setNationalFilesList(info.fileList)}
                  fileList={nationalFilesList}
                  multiple={true}
                  maxCount={2}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: !isPersonalDisabled }}
                  onRemove={() => setRemoveDocCalled(true)}
                  allowedExtensions={["jpg", "jpeg", "png"]}
                >
                  {nationalFilesList.length < 2 && (
                    <div>
                      <UploadIcon width={"20px"} />
                      <div className={"ant-upload-text"}>{t("profile.uploadNationalFiles")}</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              {isRegisterURL ? (
                <Row gutter={[8, 0]} className={"tw-w-full"}>
                  <Col span={11}>
                    <Button
                      htmlType={"submit"}
                      loading={loading || loadingSubmit}
                      className={"tw-w-full"}
                      type={"primary"}
                    >
                      {t("continue")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw-w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[8, 0]}>
                  <Col span={11}>
                    <Button
                      className={"tw-w-full"}
                      type={"primary"}
                      loading={loading || loadingSubmit}
                      htmlType={"submit"}
                    >
                      {t("save")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw-w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Col>
                </Row>
              )}
            </Form>
          </FormLayout>
        </SubLayout>
      </Page>
    </Drawer>
  );
};

export default PersonalEdit;
